import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import mixpanel from 'mixpanel-browser';
import { performLogoutCleanup } from '@/services/logout';
import auth_required from '@/routes/hooks/auth_required';
import authorized from '@/routes/hooks/authorized';
import login_external from '@/routes/hooks/login-external';
import desktopViewForced from '@/routes/hooks/desktop-view-forced';
import registerCurrency from '@/routes/hooks/register-currency';
import registerEmailVerification from '@/routes/hooks/register-email-verification';
import AppRoutes from '@/routes/app';
import Settings from '@/routes/settings';
// @ts-ignore
import NotFoundPage from '@/views/pages/404.vue';
// @ts-ignore
import LoginForm from '@/views/pages/login.vue';
// @ts-ignore
import LoginMemberForm from '@/views/pages/login-member.vue';
// @ts-ignore
import LoginSupportForm from '@/views/pages/login-support.vue';
// @ts-ignore
import RegisterForm from '@/views/pages/register.vue';
// @ts-ignore
import TwoFactor from '@/views/pages/two-factor/two-factor.vue';
// @ts-ignore
import ForgotPasswordForm from '@/views/pages/forgot-password.vue';
// @ts-ignore
import Home from '@/views/Home.vue';

import { stripReportRouteQueryFlags } from '@/components/query/report-route-query';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    ...AppRoutes,
    ...Settings,
    {
      path: '/',
      name: 'root',
      beforeEnter: [auth_required, desktopViewForced],
      component: Home,
      meta: {
        pageTitle: 'Root'
      }
    },
    {
      path: '/login',
      name: 'login',
      component: LoginForm,
      beforeEnter: [authorized, desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Login'
      }
    },
    {
      path: '/!/login',
      name: 'login_',
      component: LoginForm,
      beforeEnter: [authorized, desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Login_'
      }
    },
    {
      path: '/login/member',
      name: 'login.member',
      component: LoginMemberForm,
      beforeEnter: [desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Login member'
      }
    },
    {
      path: '/login/support',
      name: 'login.support',
      component: LoginSupportForm,
      beforeEnter: [desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Login support'
      }
    },
    {
      path: '/!/login/member',
      name: 'login.member_',
      component: LoginMemberForm,
      beforeEnter: [desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Login member_'
      }
    },
    {
      path: '/two-factor',
      name: 'two-factor',
      component: TwoFactor,
      beforeEnter: [desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Two factor authentication'
      }
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterForm,
      beforeEnter: [authorized, desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Register'
      }
    },
    {
      path: '/register-currency',
      name: 'register-currency',
      component: RegisterForm,
      beforeEnter: [desktopViewForced, registerCurrency],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Registration step 3'
      }
    },
    {
      path: '/register-email-verification',
      name: 'register-email-verification',
      component: RegisterForm,
      beforeEnter: [desktopViewForced, registerEmailVerification],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Registration step 2'
      }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordForm,
      beforeEnter: [authorized, desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Forgot password'
      }
    },
    {
      path: '/logout',
      name: 'logout',
      beforeEnter: (to: any, from: any, next: Function) => {
        performLogoutCleanup();
        return next('/login');
      },
      meta: {
        pageTitle: 'Logout'
      }
    },
    {
      //http://beta.panel.bemob.com/#/login-external?token=
      path: '/login-external',
      name: 'login.ext',
      beforeEnter: [login_external, desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Login external'
      }
    },
    {
      //http://beta.panel.bemob.com/#!/login-external?token=
      path: '/!/login-external',
      name: 'login.ext!',
      beforeEnter: [login_external, desktopViewForced],
      meta: {
        ignoreDesktopViewForced: true,
        pageTitle: 'Login external_'
      }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundPage,
      meta: {
        pageTitle: 'Not found page'
      }
    }
  ]
});

router.getPageTitle = function (this: any, route = this.currentRoute) {
  const r = route?.value ?? route;
  if (r?.name === 'app.report') {
    return `${r.meta?.pageTitle ?? 'Report'} ${r.params?.type ?? ''}`;
  }
  return r?.meta?.pageTitle ?? '';
};


const isRouteReallyChanged = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  if (to.name === 'app.report' && from.name === 'app.report' && to.params.type !== from.params.type) {
    return true;
  }

  if (to.name !== from.name) return true;

  return false;
};

router.afterEach((to) => {
  const cleanedQuery = stripReportRouteQueryFlags(to.query as Record<string, unknown>);
  if (cleanedQuery) {
    router.replace({ query: cleanedQuery });
  }
});

router.afterEach((to, from) => {
  if (!isRouteReallyChanged(to, from)) return;

  void import('@/store').then(({ useStore }) => {
    const user = useStore().user;
    if (!user) return;

    const { id, lastName = '', firstName = '', email, currencyId, language, subscriptionPlan } = user;
    const userProperty = {
      ...(email && { $email: email }),
      ...(currencyId && { currencyId }),
      ...(language && { language }),
      ...(subscriptionPlan && { subscriptionPlan }),
      ...((firstName || lastName) && { $name: `${firstName} ${lastName}` }),
      ...(firstName && { $firstName: firstName }),
      ...(lastName && { $lastName: lastName }),
    };

    if (Object.keys(userProperty).length !== 0) {
      mixpanel.people.set(userProperty);
      mixpanel.identify(id);
    }

    mixpanel.track('Page View', {
      pageTitle: router.getPageTitle(to),
    });
  });
});

export default router